import React, { useEffect, useState } from "react";
import { SwissDateField } from "@/components/SwissDateInput";
import { apiClient } from "@/lib/apiClient";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Wallet, Building2, BarChart3, AlertTriangle, Save, Loader2 } from "lucide-react";
import { toast } from "sonner";
import AdminAiStatementsList from "@/components/admin/AdminAiStatementsList";

const chf = (v) => Number(v || 0).toLocaleString("de-CH", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const PROVIDER_LABEL = { openai: "OpenAI", anthropic: "Anthropic", gemini: "Gemini", google_vision: "Vision", google_docai: "DocAI", whisper: "Whisper" };

export default function AdminAiUsageCard() {
  const [credit, setCredit] = useState(null);
  const [days, setDays] = useState([]);
  const [byCompany, setByCompany] = useState(null);
  const [range, setRange] = useState(30);
  const [amount, setAmount] = useState("");
  const [since, setSince] = useState("");
  const [saving, setSaving] = useState(false);
  const [adminKey, setAdminKey] = useState("");
  const [ym, setYm] = useState(new Date().toISOString().slice(0, 7));
  const [markup, setMarkup] = useState("0");
  const [statement, setStatement] = useState(null);
  const [stBusy, setStBusy] = useState(false);

  const saveAdminKey = async (remove = false) => {
    try {
      const r = await apiClient.put("/admin/ai-credit/admin-key", { api_key: remove ? null : adminKey });
      setCredit(r.data); setAdminKey("");
      toast.success(remove ? "Admin-Key entfernt" : "Admin-Key gespeichert");
    } catch (e) { toast.error(e.response?.data?.detail || "Fehler"); }
  };

  const runStatement = async (persist) => {
    if (persist && !window.confirm(`KI-Kosten ${ym} als Positionen für alle Mandanten übernehmen?`)) return;
    setStBusy(true);
    try {
      const r = await apiClient.post(`/admin/ai-usage/statement/${ym}`, { markup_pct: Number(markup || 0), persist });
      setStatement(r.data);
      if (persist) toast.success(`${r.data.items.length} Positionen übernommen · total CHF ${chf(r.data.total_amount_chf)}`);
    } catch (e) { toast.error(e.response?.data?.detail || "Fehler"); }
    finally { setStBusy(false); }
  };

  const load = async (r = range) => {
    try {
      const [c, d, b] = await Promise.all([
        apiClient.get("/admin/ai-credit"),
        apiClient.get(`/admin/ai-usage/days?days=${r}`),
        apiClient.get(`/admin/ai-usage/by-company?days=${r}`),
      ]);
      setCredit(c.data); setDays(d.data); setByCompany(b.data);
      if (c.data.credit_usd) setAmount(String(c.data.credit_usd));
      if (c.data.since) setSince(String(c.data.since).slice(0, 10));
    } catch { /* ignore */ }
  };
  useEffect(() => { load(range); }, [range]);

  const saveCredit = async () => {
    setSaving(true);
    try {
      const r = await apiClient.put("/admin/ai-credit", { amount_usd: Number(amount || 0), since: since ? `${since}T00:00:00+00:00` : null });
      setCredit(r.data);
      toast.success(r.data.warned_now ? "Guthaben gespeichert – Warnung ausgelöst" : "Guthaben gespeichert");
    } catch (e) { toast.error(e.response?.data?.detail || "Speichern fehlgeschlagen"); }
    finally { setSaving(false); }
  };

  const pct = credit?.used_pct ?? null;
  const barColor = pct == null ? "bg-slate-300" : pct >= 95 ? "bg-rose-500" : pct >= 80 ? "bg-amber-500" : "bg-emerald-500";
  const maxDay = Math.max(0.01, ...days.map((d) => d.total_chf));
  const openaiDays = days.map((d) => ({ day: d.day, chf: d.providers?.openai?.chf || 0, total: d.total_chf }));

  return (
    <Card className="p-5 border border-slate-200 bg-white shadow-sm mt-4" data-testid="admin-ai-usage-card">
      <div className="flex items-start gap-3 mb-4">
        <Wallet className="w-5 h-5 text-slate-700 mt-0.5" />
        <div className="flex-1">
          <h3 className="text-sm font-semibold text-slate-900">OpenAI-Guthaben &amp; KI-Verbrauch</h3>
          <p className="text-xs text-slate-500">OpenAI stellt keinen Guthaben-Abruf bereit – tragen Sie das eingezahlte Guthaben ein; die App rechnet den Verbrauch dagegen und warnt bei 80 %, 95 % und unter 7 Tagen Reichweite.</p>
        </div>
        <div className="flex gap-1">
          {[30, 90].map((r) => (
            <Button key={r} size="sm" variant={range === r ? "default" : "outline"} className="rounded-full text-xs" onClick={() => setRange(r)} data-testid={`ai-usage-range-${r}`}>{r} Tage</Button>
          ))}
        </div>
      </div>

      {/* Guthaben */}
      <div className="grid md:grid-cols-[1fr_auto] gap-4 items-end mb-5" data-testid="ai-credit-section">
        <div>
          <div className="flex justify-between text-xs text-slate-600 mb-1">
            <span>{credit?.credit_usd ? <>Eingezahlt <b>USD {chf(credit.credit_usd)}</b> seit {String(credit.since).slice(0, 10)}</> : "Noch kein Guthaben eingetragen"}</span>
            {credit?.credit_usd ? <span data-testid="ai-credit-status">verbraucht USD {chf(credit.used_usd)} ({pct} %) · Rest USD {chf(credit.remaining_usd)} · Ø {chf(credit.avg_per_day_usd)}/Tag{credit.days_left != null ? ` · reicht ca. ${credit.days_left} Tage` : ""}</span> : null}
          </div>
          <div className="h-2.5 w-full rounded-full bg-slate-100 overflow-hidden">
            <div className={`h-full ${barColor} transition-all`} style={{ width: `${Math.min(100, pct || 0)}%` }} />
          </div>
          <div className="mt-1 text-[11px] text-slate-500" data-testid="ai-credit-source">Verbrauchsquelle: {credit?.cost_source || "—"}{credit?.admin_key_error ? ` · Admin-Key-Fehler: ${credit.admin_key_error}` : ""}</div>
          {pct != null && pct >= 80 && (
            <div className="mt-1.5 flex items-center gap-1 text-xs text-amber-800" data-testid="ai-credit-warning"><AlertTriangle className="w-3.5 h-3.5" /> Bald aufladen: <a className="underline" href="https://platform.openai.com/settings/organization/billing" target="_blank" rel="noreferrer">OpenAI Billing</a></div>
          )}
        </div>
        <div className="flex items-end gap-2">
          <div><label className="text-[11px] text-slate-500">Guthaben USD</label><Input className="h-8 w-28 text-xs" type="number" step="1" value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="ai-credit-amount" /></div>
          <div><label className="text-[11px] text-slate-500">eingezahlt am</label><SwissDateField className="h-8 w-36 text-xs"  value={since} onChange={(e) => setSince(e.target.value)} data-testid="ai-credit-since" /></div>
          <Button size="sm" onClick={saveCredit} disabled={saving} data-testid="ai-credit-save">{saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}</Button>
        </div>
      </div>

      {/* OpenAI Admin-Key: echte Kosten statt Schätzung */}
      <div className="mb-5 flex flex-wrap items-end gap-2 text-xs" data-testid="ai-admin-key-section">
        <div className="flex-1 min-w-[260px]">
          <label className="text-[11px] text-slate-500">OpenAI Admin-Key (sk-admin-…) – liest die echten Kosten der Organisation; ein Guthaben-Abruf wird von OpenAI derzeit nicht angeboten</label>
          <Input className="h-8 text-xs" type="password" placeholder={credit?.admin_key_set ? "hinterlegt – neuen Key eingeben zum Ersetzen" : "sk-admin-…"} value={adminKey} onChange={(e) => setAdminKey(e.target.value)} data-testid="ai-admin-key-input" />
        </div>
        <Button size="sm" variant="outline" onClick={() => saveAdminKey(false)} disabled={!adminKey} data-testid="ai-admin-key-save">Speichern</Button>
        {credit?.admin_key_set && <Button size="sm" variant="ghost" onClick={() => saveAdminKey(true)} data-testid="ai-admin-key-remove">Entfernen</Button>}
      </div>

      {/* Weiterverrechnung */}
      <div className="mb-5 rounded-md border border-slate-200 p-3" data-testid="ai-statement-section">
        <div className="flex flex-wrap items-end gap-2 text-xs">
          <div><label className="text-[11px] text-slate-500">Monat</label><Input className="h-8 w-36 text-xs" type="month" value={ym} onChange={(e) => setYm(e.target.value)} data-testid="ai-statement-month" /></div>
          <div><label className="text-[11px] text-slate-500">Aufschlag %</label><Input className="h-8 w-20 text-xs" type="number" value={markup} onChange={(e) => setMarkup(e.target.value)} data-testid="ai-statement-markup" /></div>
          <Button size="sm" variant="outline" onClick={() => runStatement(false)} disabled={stBusy} data-testid="ai-statement-preview">Vorschau</Button>
          <Button size="sm" onClick={() => runStatement(true)} disabled={stBusy} data-testid="ai-statement-persist">Als Positionen übernehmen</Button>
          <a className="text-indigo-600 hover:underline ml-auto" href={`${process.env.REACT_APP_BACKEND_URL}/api/admin/ai-usage/statement/${ym}.csv`} target="_blank" rel="noreferrer" data-testid="ai-statement-csv">CSV</a>
        </div>
        <p className="text-[11px] text-slate-500 mt-1">Weiterverrechnung: KI-Kosten je Mandant für den Monat, auf 5 Rp. gerundet. «Übernehmen» legt pro Mandant eine Position «KI-Nutzung» ab, die der Mandant in seiner Auswertung sieht.</p>
        {statement && (
          <table className="w-full text-xs mt-2" data-testid="ai-statement-table">
            <thead><tr className="text-left text-slate-500 border-b border-slate-100"><th className="py-1">Mandant</th><th className="text-right">Aufrufe</th><th className="text-right">Kosten CHF</th><th className="text-right">Betrag CHF</th><th>Position</th></tr></thead>
            <tbody>
              {statement.items.map((i) => (
                <tr key={i.company_id} className="border-b border-slate-50"><td className="py-1">{i.name}</td><td className="text-right">{i.calls}</td><td className="text-right">{chf(i.cost_chf)}</td><td className="text-right font-medium">{chf(i.amount_chf)}</td><td className="text-slate-500 truncate max-w-[280px]">{i.position_text}</td></tr>
              ))}
              <tr><td className="py-1 font-medium">Total</td><td></td><td className="text-right font-medium">{chf(statement.total_cost_chf)}</td><td className="text-right font-medium">{chf(statement.total_amount_chf)}</td><td className="text-slate-500">{statement.persisted ? "übernommen" : "Vorschau"}</td></tr>
            </tbody>
          </table>
        )}
        <AdminAiStatementsList ym={ym} refreshKey={statement?.persisted ? statement.year_month + statement.total_amount_chf : ym} />
      </div>

      {/* Tagesverlauf */}
      <div className="mb-5" data-testid="ai-usage-days">
        <div className="flex items-center gap-1.5 text-xs font-medium text-slate-700 mb-1"><BarChart3 className="w-3.5 h-3.5" /> Verbrauch pro Tag (CHF, dunkel = OpenAI-Anteil)</div>
        <div className="flex items-end gap-[2px] h-20">
          {openaiDays.map((d) => (
            <div key={d.day} className="flex-1 h-full flex flex-col justify-end" title={`${d.day}: CHF ${chf(d.total)} (OpenAI ${chf(d.chf)})`}>
              <div className="bg-slate-200 rounded-t-sm relative" style={{ height: `${(d.total / maxDay) * 100}%` }}>
                <div className="absolute bottom-0 left-0 right-0 bg-slate-800 rounded-t-sm" style={{ height: `${d.total ? (d.chf / d.total) * 100 : 0}%` }} />
              </div>
            </div>
          ))}
        </div>
        <div className="text-[10px] text-slate-400 flex justify-between"><span>{openaiDays[0]?.day}</span><span>{openaiDays[openaiDays.length - 1]?.day}</span></div>
      </div>

      {/* Je Mandant */}
      {byCompany && (
        <div data-testid="ai-usage-by-company">
          <div className="flex items-center gap-1.5 text-xs font-medium text-slate-700 mb-1"><Building2 className="w-3.5 h-3.5" /> KI-Verbrauch je Mandant ({byCompany.days} Tage) · total CHF {chf(byCompany.total_chf)}</div>
          <table className="w-full text-xs">
            <thead><tr className="text-left text-slate-500 border-b border-slate-100"><th className="py-1">Mandant</th><th className="text-right">CHF</th><th className="text-right">Anteil</th><th className="text-right">Aufrufe</th><th className="text-right">Tokens ein/aus</th><th>Provider</th></tr></thead>
            <tbody>
              {byCompany.items.map((i) => (
                <tr key={`${i.company_id || "system"}-${i.name}`} className="border-b border-slate-50" data-testid={`ai-usage-company-${i.company_id || "system"}`}>
                  <td className="py-1 text-slate-800">{i.name}</td>
                  <td className="text-right font-medium">{chf(i.total_chf)}</td>
                  <td className="text-right text-slate-600">{i.share_pct} %</td>
                  <td className="text-right text-slate-600">{i.calls}</td>
                  <td className="text-right text-slate-500">{(i.input_tokens / 1000).toFixed(0)}k / {(i.output_tokens / 1000).toFixed(0)}k</td>
                  <td className="text-slate-500">{Object.entries(i.providers).map(([p, v]) => `${PROVIDER_LABEL[p] || p} ${chf(v.chf)}`).join(" · ")}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}
